import {
  IconDashboard,
  IconCases,
  IconMerchants,
  IconCourier,
  IconPricing,
  IconFeedback,
  IconCard,
  IconRefresh,
  IconMail,
  IconLink
} from './icons';

/**
 * Single source of truth for admin navigation.
 *
 * The sidebar renders these groups, and the topbar looks up the current route
 * here for its title/description — so a page's name is defined exactly once.
 */
export const navSections = [
  {
    label: 'Overview',
    items: [
      {
        to: '/admin/dashboard',
        label: 'Dashboard',
        icon: IconDashboard,
        title: 'Dashboard',
        description: 'Case volume, merchant activity and delivery issue breakdowns.'
      }
    ]
  },
  {
    label: 'Operations',
    items: [
      {
        to: '/admin/cases',
        label: 'Cases',
        icon: IconCases,
        title: 'Case management',
        description: 'Track, filter and progress every delivery issue raised.'
      },
      {
        to: '/admin/merchants',
        label: 'Merchants',
        icon: IconMerchants,
        title: 'Merchant management',
        description: 'Plans, usage against allowance, and account status.'
      },
      /**
       * Two different things that both involve the word "courier", so both
       * labels say which one they are:
       *
       *  - Carrier list: the `Courier` lookup table, i.e. the names in the
       *    "choose your courier" dropdown on the public report form. Nobody
       *    pays for anything here.
       *  - Courier customers: `CourierAccount` records — courier companies
       *    that signed up, with a plan, billing and a portal login.
       *
       * They join on CourierAccount.courier, the carrier name an admin links
       * an account to.
       */
      {
        to: '/admin/courier-intelligence',
        label: 'Carrier list',
        icon: IconCourier,
        title: 'Carrier list',
        description:
          'The couriers customers can pick on the report form, with case volume and response times for each.'
      },
      {
        to: '/admin/courier-accounts',
        label: 'Courier customers',
        icon: IconCourier,
        title: 'Courier customers',
        description:
          'Courier companies with a ParcelBuddy account — their plan, billing and which carrier they see.'
      }
    ]
  },
  {
    label: 'Billing',
    items: [
      {
        to: '/admin/subscriptions',
        label: 'Subscriptions',
        icon: IconRefresh,
        title: 'Subscriptions',
        description: 'Merchants and couriers on paid billing — plan, Stripe status and cancellations.'
      },
      {
        to: '/admin/transactions',
        label: 'Transactions',
        icon: IconCard,
        title: 'Transactions',
        description: 'Every payment taken — merchant and courier, newest first.'
      },
      {
        to: '/admin/pricing',
        label: 'Pricing & plans',
        icon: IconPricing,
        title: 'Pricing & plans',
        description: 'Merchant and courier plan pricing, SMS and Ship24 controls.'
      }
    ]
  },
  {
    label: 'Insight',
    items: [
      {
        to: '/admin/acquisition',
        label: 'Acquisition',
        icon: IconLink,
        title: 'Acquisition tracking',
        description: 'Where customer reports come from — Reddit and other campaigns, with cost per report.'
      },
      {
        to: '/admin/feedback',
        label: 'Feedback',
        icon: IconFeedback,
        title: 'Feedback',
        description: 'Ratings and comments from customers and merchants.'
      },
      {
        to: '/admin/contact-requests',
        label: 'Contact requests',
        icon: IconMail,
        title: 'Contact requests',
        description: 'Messages submitted through the public contact form.'
      }
    ]
  }
];

export const navItems = navSections.flatMap((section) => section.items);

const fallbackMeta = {
  title: 'Admin',
  description: 'ParcelBuddy administration.'
};

export const getPageMeta = (pathname) =>
  navItems.find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`)) || fallbackMeta;
